import { BankingEntitiesBankAccountNumberFormatEnum } from 'generated/resources/datatable.graphql'
import {
  EmployeeBlocksEmployeeFragment,
  SetBankingInformationDocument,
} from 'generated/resources/graphql'

import { useEffect } from 'modules/core/hooks/useEffect'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  accountNumberExtraFieldLabel,
  accountNumberLabel,
  bankAccountFormatOptions,
  defaultBankNumberFormat,
} from 'modules/banking/lib/bankAccountNumbersHelpers'

type SetBankInformationFormValues = {
  accountNumber: string
  complementaryData: string
  format: BankingEntitiesBankAccountNumberFormatEnum
  employeeId: number
}

export const useBankInfoForm = (employee: EmployeeBlocksEmployeeFragment, readonly: boolean) => {
  const { editEmployeePersonalBankInfoSubmit } = trackingEvents.employee.personal
  const [setBankingInformation, { loading: submitting }] = useMutation(
    SetBankingInformationDocument,
    {
      extractorFn: (data) => data.bankingMutations.upsertBankingBankAccountNumber,
      update: (cache, { data }) => {
        const mutationResponse = data?.bankingMutations.upsertBankingBankAccountNumber
        const bankAccountNumber = mutationResponse?.bankAccountNumber

        if (bankAccountNumber) {
          cache.modify({
            id: cache.identify({ id: employee.id, __typename: 'EmployeesEmployee' }),
            fields: {
              bankNumber: () => bankAccountNumber.accountNumber,
              bankNumberFormat: () => bankAccountNumber.format,
              swiftBic: () => bankAccountNumber.complementaryData,
            },
          })
        }
      },
    }
  )

  const countryCode = employee.country?.toString().toLowerCase() ?? ''
  const defaultFormat: BankingEntitiesBankAccountNumberFormatEnum =
    employee.bankNumberFormat || defaultBankNumberFormat(countryCode)

  const defaultValues: SetBankInformationFormValues = {
    accountNumber: employee.bankNumber || '',
    complementaryData: employee.swiftBic || '',
    format: defaultFormat,
    employeeId: employee.id,
  }

  const form = useForm<SetBankInformationFormValues>({
    defaultValues,

    validate: (data) => {
      const errors: FormErrors<SetBankInformationFormValues> = {}
      if (!data.accountNumber) {
        errors.accountNumber = i18n.t('employee_form.bank_info.mandatory')
      }
      if (
        (data.format === 'sort_code_and_account_number' ||
          data.format === 'routing_number_and_account_number' ||
          data.format === 'bank_name_and_account_number') &&
        !data.complementaryData
      ) {
        errors.complementaryData = i18n.t('employee_form.bank_info.mandatory')
      }
      return errors
    },

    onSubmit: async (values) => {
      const variables = {
        accountNumber: values.accountNumber,
        complementaryData: values.complementaryData,
        format: values.format,
        employeeId: employee.id,
      }

      const result = await setBankingInformation({ variables })

      track(editEmployeePersonalBankInfoSubmit, {
        fields_edited: Object.keys(form.touched),
        fields_completed: Object.keys(form.values).filter(
          (key) => !!form.values[key as keyof SetBankInformationFormValues]
        ),
        legal_entity_country: employee.legalEntity?.country,
      })

      const handled = handleResult(result, (errors) => {
        form.setErrors({
          accountNumber: errors['accountNumber'],
          complementaryData: errors['complementaryData'],
        })
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('employee_form.bank_info.saved_successfully'),
        })
        form.resetForm({ values })
      } else {
        form.setFieldError('accountNumber', i18n.t('employee_form.bank_info.invalid_format'))

        if (form.values.complementaryData) {
          form.setFieldError('complementaryData', i18n.t('employee_form.bank_info.invalid_format'))
        }

        app.setFlash({
          type: 'wrong',
          message: i18n.t('errors.generic_saving'),
        })
      }
      return handled
    },
  })

  useEffect(() => {
    form.resetForm({
      values: defaultValues,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee.id])

  const hasErrors = Object.values(form.errors).filter((e) => !!e).length > 0
  const accountLabel = accountNumberLabel(form.values.format)
  const extraFieldLabel = accountNumberExtraFieldLabel(form.values.format)
  const showSubmit = !readonly && (form.dirty || hasErrors || submitting)

  return {
    form,
    submitting,
    bankAccountFormatOptions,
    accountLabel,
    extraFieldLabel,
    showSubmit,
  }
}
